import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

export default function PaymentResult() {
  const [params] = useSearchParams();
  const txnid = params.get("txnid");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!txnid) {
      setErr("Missing transaction reference.");
      return;
    }
    fetch(`${API}/payments/status/${txnid}`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.detail || "Transaction not found");
        setData(d);
      })
      .catch((e) => setErr(e.message));
  }, [txnid]);

  const paid = data?.status === "paid";

  return (
    <main
      className="min-h-screen bg-[#161614] text-[#f0eee4] font-body antialiased flex items-center justify-center px-6"
      data-testid="payment-result"
    >
      <div className="w-full max-w-lg border border-[#2e2e2a] bg-[#1e1e1c] p-8 lg:p-10">
        <p className="font-mono text-[10px] tracking-[0.35em] text-[#6e6e6a]">TORQ CIRCUIT — DELHI</p>

        {!data && !err && (
          <div className="mt-10 flex items-center gap-3 font-mono text-xs tracking-[0.2em] text-[#a3a39f]" data-testid="payment-result-loading">
            <Loader2 size={16} className="animate-spin" /> CHECKING YOUR PAYMENT…
          </div>
        )}

        {err && (
          <div data-testid="payment-result-error">
            <span className="mt-8 inline-block border border-[#454540] p-3">
              <X size={18} />
            </span>
            <h1 className="mt-6 font-display font-extrabold uppercase text-4xl leading-none">
              CAN'T FIND IT.
            </h1>
            <p className="mt-3 text-sm text-[#a3a39f]">{err}</p>
          </div>
        )}

        {data && (
          <div data-testid="payment-result-panel">
            <span className="mt-8 inline-block border border-[#f0eee4] p-3">
              {paid ? <Check size={18} /> : <X size={18} />}
            </span>
            <h1
              className="mt-6 font-display font-extrabold uppercase text-4xl leading-none"
              data-testid="payment-result-status"
            >
              {paid ? "PAYMENT CONFIRMED." : "PAYMENT NOT COMPLETED."}
            </h1>
            {paid ? (
              <p className="mt-3 text-sm text-[#a3a39f] leading-relaxed">
                {data.name?.split(" ")[0]}, your place on the Delhi start line is locked in. A
                confirmation email is on its way to your inbox.
              </p>
            ) : (
              <p className="mt-3 text-sm text-[#a3a39f] leading-relaxed">
                No charge was confirmed for this transaction. Head back and try again — your details
                take ten seconds.
              </p>
            )}
            <div className="mt-8 border-t border-[#2e2e2a] font-mono text-[11px] tracking-[0.15em]">
              <div className="flex justify-between py-3 border-b border-[#2e2e2a]">
                <span className="text-[#6e6e6a]">DIVISION</span>
                <span data-testid="payment-result-division">{data.division_label}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-[#2e2e2a]">
                <span className="text-[#6e6e6a]">AMOUNT</span>
                <span data-testid="payment-result-amount">{inr(data.amount_paise || 0)}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-[#2e2e2a]">
                <span className="text-[#6e6e6a]">TRANSACTION</span>
                <span className="break-all text-right" data-testid="payment-result-ref">{data.txnid}</span>
              </div>
              {data.payment_id && (
                <div className="flex justify-between py-3">
                  <span className="text-[#6e6e6a]">PAYU REF</span>
                  <span data-testid="payment-result-mihpayid">{data.payment_id}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <Link
          to="/"
          data-testid="payment-result-back"
          className="btn-solid block text-center mt-8 bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white"
        >
          {paid ? "BACK TO TORQ CIRCUIT" : "TRY AGAIN"}
        </Link>
      </div>
    </main>
  );
}
